/**
 * Core Color Tokens — Tier 1
 *
 * Raw color palette. No semantic meaning.
 * All names are identical to Figma token names — one source of truth.
 *
 * Rules:
 * - No raw hex values anywhere else in the codebase — only here
 * - Tier 2 semantic tokens reference these keys; never raw hex values
 */

export const colors = {
  // Primary Blue
  'primary-50': '#E1F5FE',
  'primary-100': '#B3E5FC',
  'primary-200': '#81D4FA',
  'primary-300': '#4FC3F7',
  'primary-400': '#29B6F6',
  'primary-500': '#01579B', // main
  'primary-600': '#014A86',
  'primary-700': '#003B5C', // dark
  'primary-800': '#002F4A',
  'primary-900': '#002437',
  'primary-contrast': '#FFFFFF',

  // Secondary Blue
  'secondary-50': '#E0F7FA',
  'secondary-100': '#B2EBF2',
  'secondary-200': '#80DEEA',
  'secondary-300': '#4DD0E1',
  'secondary-400': '#26C6DA',
  'secondary-500': '#00B5E2', // main
  'secondary-600': '#009FC6',
  'secondary-700': '#007E9E', // dark
  'secondary-800': '#00697F',
  'secondary-900': '#004D5E',
  'secondary-contrast': '#FFFFFF',

  // Neutral / Gray
  'neutral-50': '#DED4D7',
  'neutral-100': '#BFC0C4',
  'neutral-200': '#A7A8AC',
  'neutral-300': '#909195',
  'neutral-400': '#7A7B7E',
  'neutral-500': '#666666',
  'neutral-600': '#55575A',
  'neutral-700': '#47484B',
  'neutral-800': '#3F4042',
  'neutral-900': '#333333',

  // White
  white: '#FFFFFF',

  // Success Green — structure: 400=light, 500=main, 700=dark
  'success-50': '#E6F4EA',
  'success-100': '#CCE9D5',
  'success-200': '#99D3AB',
  'success-300': '#66BD81',
  'success-400': '#339933', // light
  'success-500': '#008000', // main
  'success-600': '#006600',
  'success-700': '#005900', // dark
  'success-800': '#004000',
  'success-900': '#002600',

  // Warning Yellow — structure: 400=light, 500=main, 700=dark
  'warning-50': '#FFF8E6',
  'warning-100': '#FDEFC2',
  'warning-200': '#FBE08C',
  'warning-300': '#F9D056',
  'warning-400': '#F7CF63', // light
  'warning-500': '#F6C44A', // main
  'warning-600': '#D9AC41',
  'warning-700': '#AC8933', // dark
  'warning-800': '#7D6526',
  'warning-900': '#4D3E17',

  // Error Red — structure: 400=light, 500=main, 700=dark
  'error-50': '#FDECEA',
  'error-100': '#FBD1CB',
  'error-200': '#F7A39A',
  'error-300': '#F57568',
  'error-400': '#F36F57', // light
  'error-500': '#F14C2E', // main
  'error-600': '#D94429',
  'error-700': '#A83520', // dark
  'error-800': '#7A2718',
  'error-900': '#4D1810',

  // Info Blue — structure: 400=light, 500=main, 700=dark
  // info-900 is TBD — pending designer input (Barry Richardson)
  'info-50': '#EEF3FD',
  'info-100': '#D4E1FA',
  'info-200': '#A9C3F4',
  'info-300': '#7EA5EF',
  'info-400': '#77A1EC', // light
  'info-500': '#558AE8', // main
  'info-600': '#4C7CD1',
  'info-700': '#3B60A2', // dark
  'info-800': '#2C4778',
  'info-900': '#1B2B4A',

  // Tertiary / Data Visualization
  // NOTE: Not yet formally defined in the Figma DLS. Actively used in cohort
  // charts. TODO: Ask Barry to review — he may reorganise these into a
  // dedicated dataviz palette.
  'tertiary-light-blue': '#51B2D9',
  'tertiary-blue': '#3065A3',
  'tertiary-dark-blue': '#153A59',
  'tertiary-green': '#4AA596',
  'tertiary-cyan': '#50AFBD',
  'tertiary-yellow': '#F5C844',
  'tertiary-orange': '#DB7934',
  'tertiary-light-gray': '#ACC0C7',
  'tertiary-black': '#222934',
} as const;

export type Colors = typeof colors;
export type ColorToken = keyof Colors;
